import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWsStore, AckEntry } from '../store/wsStore';

export const MobileController: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const {
    wsStatus,
    ackStates,
    sessionExpired,
    appDisconnected,
    lastError,
    connect,
    disconnect,
    sendMessage,
    clearAcks,
  } = useWsStore();

  const [text, setText] = useState('');
  const [expandedAckId, setExpandedAckId] = useState<string | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const deferredPromptRef = useRef<any>(null);

  // On mount: set meta tags & listen for beforeinstallprompt event
  useEffect(() => {
    let robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'noindex, nofollow';

    let referrerMeta = document.querySelector<HTMLMetaElement>('meta[name="referrer"]');
    if (!referrerMeta) {
      referrerMeta = document.createElement('meta');
      referrerMeta.name = 'referrer';
      document.head.appendChild(referrerMeta);
    }
    referrerMeta.content = 'no-referrer';

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // On mount: connect if token exists, disconnect on unmount
  useEffect(() => {
    if (token) {
      connect(token);
    }
    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  const handleInstallClick = async () => {
    if (deferredPromptRef.current) {
      deferredPromptRef.current.prompt();
      const choiceResult = await deferredPromptRef.current.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setCanInstall(false);
      }
      deferredPromptRef.current = null;
    }
  };

  const handleAction = (command: string) => {
    if (!text && command !== 'clear') return;
    sendMessage(text, command);
    if (command !== 'copy') {
      setText('');
    }
  };

  const handleClear = () => {
    setText('');
  };

  // State 1 & 2: Session expired or no token in URL
  if (sessionExpired || !token) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 font-sans">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mx-auto">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-white tracking-tight">Session Expired</h1>
            <p className="text-sm text-slate-400">
              This session is no longer active — scan a new QR code from the Windows app
            </p>
          </div>
        </div>
      </div>
    );
  }

  // State 3: Connecting or Error
  if (wsStatus === 'connecting') {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 font-sans">
        <div className="flex flex-col items-center space-y-4 text-center">
          <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
          <p className="text-sm text-slate-400 font-medium">Connecting to session...</p>
        </div>
      </div>
    );
  }

  if (wsStatus === 'error') {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 font-sans">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center mx-auto">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-bold text-white">Connection Error</h1>
            <p className="text-sm text-slate-400">
              {lastError || 'Unable to connect to the relay server.'}
            </p>
          </div>
          <button
            onClick={() => connect(token)}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white rounded-xl font-medium text-sm transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // State 4: Normal active session UI
  const charCount = text.length;
  const maxChars = 100000;
  let charCounterColor = 'text-slate-400';
  if (charCount > 99000) {
    charCounterColor = 'text-red-400 font-semibold';
  } else if (charCount > 90000) {
    charCounterColor = 'text-amber-400 font-semibold';
  }

  const ackEntries = Object.values(ackStates).reverse().slice(0, 10);

  const getStatusDot = (status: AckEntry['status']) => {
    switch (status) {
      case 'queued':
        return <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block shrink-0" title="Queued" />;
      case 'delivered':
        return <span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block shrink-0" title="Delivered" />;
      case 'pasted':
        return <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block shrink-0" title="Pasted" />;
      case 'failed':
        return <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block shrink-0" title="Failed" />;
    }
  };

  const getCommandBadge = (command: string) => {
    const labels: Record<string, string> = {
      paste: 'Paste',
      paste_enter: 'Paste+Enter',
      paste_tab: 'Paste+Tab',
      type: 'Type',
      copy: 'Copy',
    };
    return (
      <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-slate-800 text-slate-300 border border-slate-700 shrink-0">
        {labels[command] || command}
      </span>
    );
  };

  const isConnected = wsStatus === 'connected';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* Title bar */}
      <header className="bg-slate-900/80 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold text-xs shadow-md">
            AT
          </div>
          <span className="font-bold text-lg text-white tracking-tight">AirText</span>
        </div>
        {canInstall && (
          <button
            onClick={handleInstallClick}
            className="px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 rounded-lg text-xs font-semibold transition-colors"
          >
            Install App
          </button>
        )}
      </header>

      {/* Caution banner when Windows app is disconnected */}
      {appDisconnected && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-300 px-4 py-2.5 text-xs flex items-center space-x-2 font-medium">
          <svg className="w-4 h-4 shrink-0 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Windows app disconnected — session will expire soon</span>
        </div>
      )}

      {/* Main content area */}
      <main className="flex-1 flex flex-col p-4 pb-36 max-w-2xl w-full mx-auto space-y-4">
        {/* Textarea wrapper (flex-1 grow) */}
        <div className="flex-1 flex flex-col min-h-[220px]">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type here..."
            className="flex-1 grow bg-slate-900 border border-slate-700 rounded-xl p-4 text-white placeholder-slate-500 resize-none w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base leading-relaxed"
          />
          <div className="flex justify-between items-center mt-2 px-1 text-xs">
            <span className="text-slate-500">Max 100,000 chars</span>
            <span className={charCounterColor}>
              {charCount.toLocaleString()} / {maxChars.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Recent acks list */}
        {ackEntries.length > 0 && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-slate-400 font-medium px-1">
              <span>Recent Activity</span>
              <button
                onClick={clearAcks}
                className="text-slate-500 hover:text-slate-300 text-[11px] transition-colors"
              >
                Clear log
              </button>
            </div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {ackEntries.map((entry) => {
                const isExpanded = expandedAckId === entry.message_id;
                return (
                  <div
                    key={entry.message_id}
                    onClick={() => setExpandedAckId(isExpanded ? null : entry.message_id)}
                    className="bg-slate-900 border border-slate-800 rounded-lg p-2 text-xs flex flex-col space-y-1 cursor-pointer hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-center justify-between space-x-2">
                      <div className="flex items-center space-x-2 min-w-0">
                        {getStatusDot(entry.status)}
                        {getCommandBadge(entry.command)}
                        <span className="text-slate-300 truncate">
                          {entry.content_preview || '(empty)'}
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono shrink-0">
                        {entry.message_id.slice(0, 8)}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="pt-1 border-t border-slate-800/80 text-slate-400 space-y-1 text-[11px] font-mono">
                        <div>Status: <span className="text-slate-200 capitalize">{entry.status}</span></div>
                        {entry.error && <div className="text-red-400">Error: {entry.error}</div>}
                        <div>ID: {entry.message_id}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Fixed bottom action bar */}
      <footer className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-3 z-20 shadow-2xl">
        {!isConnected ? (
          <div className="flex items-center justify-center py-2 space-x-2 text-xs text-amber-400">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span>Reconnecting to session...</span>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto flex flex-wrap gap-2 justify-center">
            {/* Paste (Primary) */}
            <button
              onClick={() => handleAction('paste')}
              disabled={!text}
              className="min-h-[48px] px-5 py-3 rounded-xl text-sm font-semibold transition-colors bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              Paste
            </button>

            {/* Paste+Enter */}
            <button
              onClick={() => handleAction('paste_enter')}
              disabled={!text}
              className="min-h-[48px] px-4 py-3 rounded-xl text-sm font-semibold transition-colors bg-indigo-500/70 hover:bg-indigo-500/90 active:bg-indigo-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Paste+Enter
            </button>

            {/* Paste+Tab */}
            <button
              onClick={() => handleAction('paste_tab')}
              disabled={!text}
              className="min-h-[48px] px-4 py-3 rounded-xl text-sm font-semibold transition-colors bg-indigo-500/70 hover:bg-indigo-500/90 active:bg-indigo-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Paste+Tab
            </button>

            {/* Type */}
            <button
              onClick={() => handleAction('type')}
              disabled={!text}
              className="min-h-[48px] px-5 py-3 rounded-xl text-sm font-semibold transition-colors bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              Type
            </button>

            {/* Copy-only */}
            <button
              onClick={() => handleAction('copy')}
              disabled={!text}
              className="min-h-[48px] px-4 py-3 rounded-xl text-sm font-semibold transition-colors bg-amber-600 hover:bg-amber-500 active:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Copy-only
            </button>

            {/* Clear */}
            <button
              onClick={handleClear}
              className="min-h-[48px] px-3 py-3 rounded-xl text-sm font-semibold transition-colors bg-slate-700 hover:bg-slate-600 text-slate-200"
            >
              Clear
            </button>
          </div>
        )}
      </footer>
    </div>
  );
};

export default MobileController;
